'use client';

import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["currentTab", "onTabChange", "children"];
import React, { createContext, useEffect, useRef, useState } from 'react';
import { generateEncoreTabId } from "../TabItem/index.mjs";
import { DOWN_ARROW, ENTER, getDataEncoreTabIdAttr, getIndex, getNextIndex, getNodeFromIndex, LEFT_ARROW, RIGHT_ARROW, SPACE } from "./utils.mjs";
import { ENCORE_DATA_IDS } from "../../dataEncoreIds.mjs";
import { mergeRefs } from "../../utils.mjs";
var tabsContextDefault = {
  tabItems: undefined,
  tabPanel: undefined,
  selectedTabItem: undefined,
  handleKeyDown: function handleKeyDown() {},
  handleClick: function handleClick() {}
};
export var TabsContext = /*#__PURE__*/createContext(tabsContextDefault);

/**
 * Tabs enable users to switch between views of related content within a single page.
 *
 * When used together, the Tab components are stateful and keyboard accessible out-of-the-box.
 *
 * [Storybook](https://encore-web.spotify.net/?path=/docs/components-tabs--docs) |
 * [Encore site](https://encore.spotify.net/web/components/tabs) |
 * [Github](https://ghe.spotify.net/encore/web/tree/master/src/core/components/Tabs) |
 * [Figma](https://www.figma.com/design/bnQXqGkLeiTlbk8JXGDcsX/%E2%9A%99%EF%B8%8F-%E2%80%8A%E2%80%8BEncore-Web-Components?m=auto&node-id=32966-1417&t=608qKxXhWG8gyhvB-1)
 */
export var Tabs = /*#__PURE__*/React.forwardRef(function Tabs(_ref, ref) {
  var currentTab = _ref.currentTab,
    onTabChange = _ref.onTabChange,
    children = _ref.children,
    props = _objectWithoutProperties(_ref, _excluded);
  /** currentTab is just the value of the TabItem, but we rely on the data-encore-tab-id attribute that is generated in TabItem to control the keyboard interactions.
   * This is to ensure that we are relying on a static value.
   * Eg: If we relied on the id generated in TabItem, the interactions would break if a customer brought their own custom id.
   */
  var encoreTabId = currentTab ? generateEncoreTabId(currentTab) : '';
  var _useState = useState(encoreTabId),
    _useState2 = _slicedToArray(_useState, 2),
    selectedTabItem = _useState2[0],
    setSelectedTabItem = _useState2[1];
  var _useState3 = useState(undefined),
    _useState4 = _slicedToArray(_useState3, 2),
    tabItems = _useState4[0],
    setTabItems = _useState4[1];
  var _useState5 = useState(undefined),
    _useState6 = _slicedToArray(_useState5, 2),
    tabPanel = _useState6[0],
    setTabPanel = _useState6[1];
  var tabsRef = useRef(null);
  function handleKeyDown(e, childTabListOnKeyDown) {
    if (childTabListOnKeyDown) {
      childTabListOnKeyDown(e);
    }
    if (!tabItems) return;
    var activeElementValue = getDataEncoreTabIdAttr(document.activeElement);
    var activeElementIndex = getIndex(activeElementValue, tabItems);
    switch (e.key) {
      case LEFT_ARROW:
      case RIGHT_ARROW:
        {
          e.preventDefault();
          var newActiveTabIndex = getNextIndex(e, activeElementIndex, tabItems === null || tabItems === void 0 ? void 0 : tabItems.size);
          var newActiveTab = getNodeFromIndex(tabItems, newActiveTabIndex);
          newActiveTab === null || newActiveTab === void 0 || newActiveTab.focus();
          newActiveTab === null || newActiveTab === void 0 || newActiveTab.scrollIntoView({
            block: 'nearest',
            inline: 'center',
            behavior: 'smooth'
          });
          return;
        }
      case DOWN_ARROW:
        {
          if (selectedTabItem) {
            var nonInteractiveTabPanel = tabPanel === null || tabPanel === void 0 ? void 0 : tabPanel.get(selectedTabItem);
            if (nonInteractiveTabPanel) {
              e.preventDefault();
              // If there are no interactive elements inside TabPanel, it would've set tabPanel with the same value (selectedTabItem) as its key in the ref callback in TabPanel
              nonInteractiveTabPanel.focus();
            }
          }
          return;
        }
      case SPACE:
      case ENTER:
        {
          var _newActiveTab = getDataEncoreTabIdAttr(e.target);
          var isDisabled = e.target.getAttribute('aria-disabled') === 'true';
          // Prevent selection if Tab is disabled
          if (isDisabled) {
            return;
          }
          // Prevent constantly updating the selectedTabItem and calling onTabChange
          if (_newActiveTab === selectedTabItem) {
            return;
          }
          setSelectedTabItem(_newActiveTab);
          if (onTabChange) {
            var _newActiveTabIndex = getIndex(_newActiveTab, tabItems);
            onTabChange(_newActiveTab, _newActiveTabIndex);
          }
          return;
        }
      default:
        {
          return;
        }
    }
  }
  function handleClick(e, newSelectedTabValue, childTabItemOnClick) {
    var _e$target;
    setSelectedTabItem(newSelectedTabValue);
    (_e$target = e.target) === null || _e$target === void 0 || _e$target.scrollIntoView({
      block: 'nearest',
      inline: 'center',
      behavior: 'smooth'
    });
    if (onTabChange && tabItems) {
      var newActiveTabIndex = getIndex(newSelectedTabValue, tabItems);
      onTabChange(newSelectedTabValue, newActiveTabIndex);
    }
    if (childTabItemOnClick) {
      childTabItemOnClick(e);
    }
  }

  /** we set the value of `selectedTabItem` in a useEffect here instead of
   * via the default value when `selecteTabItem` is first initialized so
   * that it can remain in sync with the `currentTab` prop value (for cases
   * where `currentTab` is set to something stateful like URL params)
   * */
  useEffect(function () {
    setSelectedTabItem(encoreTabId);
  }, [encoreTabId]);
  useEffect(function () {
    if (!tabItems && tabsRef.current) {
      var map = new Map();
      setTabItems(map);
    }
  }, [tabItems, tabsRef]);
  useEffect(function () {
    if (!tabPanel && tabsRef.current) {
      var map = new Map();
      setTabPanel(map);
    }
  }, [tabPanel, tabsRef]);
  useEffect(function () {
    if (tabItems && !selectedTabItem) {
      var defaultSelectedTab = tabItems.keys().next().value;
      var initialTab = currentTab ? "".concat(currentTab, "-tab") : defaultSelectedTab;
      setSelectedTabItem(initialTab);
    }
  }, [tabItems, currentTab, selectedTabItem]);
  return /*#__PURE__*/React.createElement(TabsContext.Provider, {
    value: {
      tabItems: tabItems,
      tabPanel: tabPanel,
      selectedTabItem: selectedTabItem,
      handleKeyDown: handleKeyDown,
      handleClick: handleClick
    }
  }, /*#__PURE__*/React.createElement("div", Object.assign({
    ref: mergeRefs([tabsRef, ref]),
    "data-encore-id": ENCORE_DATA_IDS.Tabs
  }, props), children));
});